import { gql, useLazyQuery } from '@apollo/client';
import { AppId } from '@skytvnz/sky-app-store/lib/types/graph-ql';
import { useEffect } from 'react';

// Define the offers fragment
export const CUSTOMER_OFFERS_FRAGMENT = gql/* GraphQL */ `
  fragment offersFragment on CustomerPackageOffer {
    id
    title
    synopsis
    heroImage {
      uri
    }
    price {
      basePrice {
        amount
      }
      billingFrequency
      discount {
        type
        value
        duration
      }
    }
    collectionLink {
      collection {
        id
        title
      }
    }
    package {
      image {
        uri
      }
    }
  }
`;

export const GET_CUSTOMER_OFFERS = gql/* GraphQL */ `
  query GetUnSubscribedPkgsWithOfferDetails($appId: AppId!) {
    experience(appId: $appId) {
      customer {
        offers {
          ...offersFragment
        }
      }
    }
  }
  ${CUSTOMER_OFFERS_FRAGMENT}
`;

// Define the subscription for unsubscribed packages event
export const CUSTOMER_UNSUBSCRIBED_PACKAGE_EVENT_SUBSCRIPTION = gql/* GraphQL */ `
  subscription customerUnSubPkgEventSubscription($appId: AppId!) {
    customerAccount(appId: $appId) {
      account {
        offers {
          ...offersFragment # Include the offers fragment here as well
        }
      }
    }
  }
  ${CUSTOMER_OFFERS_FRAGMENT}
`;

const useUnsubscribedPackages = (subscribeToChanges: boolean) => {
  const [getUnSubPkgs, result] = useLazyQuery(GET_CUSTOMER_OFFERS);

  const getUnsubscribedPackages = () => {
    getUnSubPkgs({ variables: { appId: AppId.SkyGoWeb } });
  };

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    if (subscribeToChanges && result.data && result.subscribeToMore) {
      unsubscribe = result.subscribeToMore({
        document: CUSTOMER_UNSUBSCRIBED_PACKAGE_EVENT_SUBSCRIPTION,
        variables: { appId: AppId.SkyGoWeb },
        updateQuery: (prev, { subscriptionData }) => {
          if (!subscriptionData.data) {
            return prev;
          }
          const account = subscriptionData.data?.customerAccount?.account;
          return {
            ...prev,
            experience: {
              ...prev.experience,
              customer: {
                ...prev.experience.customer,
                offers: account?.offers,
              },
            },
          };
        },
      });
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [result, subscribeToChanges]);

  return { result, getUnsubscribedPackages };
};

export default useUnsubscribedPackages;
